import ko from "knockout";
import { Dictionary } from "flow/api/types";
import { IDateParams } from "./date";
import viewHtml from "./dateV2.html";
import createRequiredObservable from "flow/koHelpers/createRequiredObservable";
import { selectLabel, selectPlaceholder } from "flow/koHelpers/selectParam";
import createEnabler from "flow/koHelpers/createEnablerDisabler";
import { ObservableAttributes } from "flow/interfaces/MaybeObservable";
import { selectIdStatic } from "flow/koHelpers/selectId";
import { interceptAttributesWithDefaultsIgnoreId } from "flow/koHelpers/interceptAttributes";
import metaCache from "flow/utils/metaCache";

export interface IDateV2Params extends IDateParams {
    event?: Dictionary;
}

type DatePickableJQueryElement = JQuery<Element> & IDatePickerControl;

interface IDatePickerControl {
    datepicker(options: JQueryUI.DatepickerOptions): DatePickableJQueryElement;
    datepicker(operation: "show" | "destroy"): DatePickableJQueryElement;
}

class DateV2ViewModel {
    value: KnockoutObservable<string>;
    valueUpdate: string;
    noOverflow: boolean;
    enableMaxWidth?: boolean;
    positionAbove?: boolean;
    attributes: ObservableAttributes;
    preventBubble: string;
    datepickerOptions?: JQueryUI.DatepickerOptions;
    enable: KnockoutObservable<boolean>;
    label: KnockoutObservable<string>;
    id: string;
    required: KnockoutObservable<boolean>;
    event: Dictionary;
    ariaLabel: KnockoutComputed<string>;

    constructor(params: IDateV2Params, input: Element) {
        this.value = params.value;
        this.valueUpdate = params.valueUpdate;
        this.label = selectLabel(params.meta, params.text);
        this.enable = createEnabler(params.enable, params.disable);
        this.required = createRequiredObservable(params.value);
        this.datepickerOptions = ko.unwrap(params.datepickerOptions) || {};
        this.preventBubble = params.preventBubble || "";
        this.noOverflow = params.noOverflow ? params.noOverflow : false;
        this.enableMaxWidth = params.enableMaxWidth ? params.enableMaxWidth : false;

        this.event = !!params.event ? params.event : {};

        this.positionAbove = params.positionAbove ? params.positionAbove : false;

        // We can't use attr to set id in this case as it needs to be set before we initialize jQuery UI datepicker.
        // Otherwise will set its own id first that will then be overwritten by attr.
        // This would cause clicking dates in calendar to cause page navigate as link behavior won't be suppressed.
        this.attributes = interceptAttributesWithDefaultsIgnoreId(
            {
                autocomplete: "off",
                placeholder: selectPlaceholder(params.metaPlaceholder, params.placeholder)
            },
            params.attr
        );

        this.ariaLabel = ko.pureComputed(() => {
            return this.value()
                ? `${metaCache.translate("FlowHtml.Translations.ChangeDate")}, ${this.value()}`
                : `${metaCache.translate("FlowHtml.Translations.ChooseDate")}`;
        });

        // We can't allow id to change due to reasons listed above. So we take a snapshot and use that.
        this.id = selectIdStatic("ko_date_v2", params.attr);
        input.setAttribute("id", this.id);
    }
}

ko.components.register("ko-datev2", {
    viewModel: {
        createViewModel(params: any, componentInfo: KnockoutComponentTypes.ComponentInfo) {
            const input = (componentInfo.element as Element).querySelector("input");
            return new DateV2ViewModel(params, input);
        }
    },
    template: viewHtml,
    synchronous: true
});
